import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { noop } from 'lodash'
import { useGroupDetailsQuery, useSocketIoClient } from '@mwxltd/flix-api-client'
import { playbackEvents } from '@flix/common/domain/video/Player/playbackEvents'
import { useButtonHandlers } from '@flix/common/elements/controls'
import { formatTime } from '@flix/common/utils/secondsToDuration'
import { useActiveUserSync } from './useActiveUserSync'
import { useGroupChat } from './useGroupChat'
import { useGroupVideoSync } from './useGroupVideoSync'
import { useGroupWatchVisibility } from './useGroupWatchVisibility'

const EVENTS = {
  JOIN: 'join',
  CHAT: 'chat',
  PLAYBACK_STATUS: 'playback-status',
  CLOSE: 'close',
  GET_ACTIVE_USERS: 'get-active-users',
  ACTIVE_STATE: 'active-state',
}

const maxRefetches = 5
const thisDeviceId = Math.random().toString()

export const useGroupWatchRoom = ({
  authStatus,
  groupWatchId,
  initialSyncEnabled,
  onGroupIsNotLive,
  onReconnectFailed = noop,
  isTV = false,
}) => {
  const [userIsWatchingOnTV, setUserIsWatchingOnTV] = useState(isTV)

  const refetchCount = useRef(0)
  const mounted = useRef(true)

  useEffect(() => {
    return () => {
      mounted.current = false
    }
  }, [])

  const {
    data: { groupWatch } = {},
    loading: groupDetailsLoading,
    error: groupWatchError,
    refetch: refetchGroup,
  } = useGroupDetailsQuery(groupWatchId, { fetchPolicy: 'network-only' })

  const { addNewMessage, chatMessages } = useGroupChat(groupWatch)

  const socketQuery = useMemo(
    () => ({ groupWatchId, isTV, deviceId: thisDeviceId }),
    [groupWatchId, isTV],
  )
  const { socket, socketConnected } = useSocketIoClient({
    connectionEnabled: !!groupWatchId,
    memoizedQuery: socketQuery,
  })

  const handleRefetchGroupWatch = useCallback(() => {
    if (!mounted.current) return Promise.resolve(groupWatch)

    refetchCount.current++
    return refetchGroup().then((res) => res.data.groupWatch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchGroup])

  const { handlers: buttonHandlers } = useButtonHandlers()

  const {
    initialSyncComplete,
    onPlaybackStatusChange,
    resyncVideo,
    resyncVideoAndPause,
    videoPlaybackStatus,
  } = useGroupVideoSync({
    initialSyncEnabled,
    refetchGroup: handleRefetchGroupWatch,
  })

  const sendChatMessage = useCallback(
    (text) =>
      socket?.emit(EVENTS.CHAT, {
        text,
        memberId: authStatus.id,
      }),
    [socket, authStatus.id],
  )

  const emitActiveState = useCallback(
    (active) => socket?.emit(EVENTS.ACTIVE_STATE, { userId: authStatus.id, active, isTV }),
    [isTV, socket, authStatus.id],
  )

  const websocketHandlers = useMemo(
    () => ({
      emitActiveState,
      emitClose: () => socket?.emit(EVENTS.CLOSE),
      emitPlaybackStatusChange: (status) => {
        if (initialSyncComplete()) {
          socket?.emit(EVENTS.PLAYBACK_STATUS, status)
          sendChatMessage(`${playbackStatusTextMap[status.type]} ${formatTime(status.seekTime)}`)
        }
      },
      emitGetActiveUsers: () => socket?.emit(EVENTS.GET_ACTIVE_USERS),
      emitJoin: () => socket?.emit(EVENTS.JOIN),
      sendChatMessage,
    }),
    [emitActiveState, sendChatMessage, socket, initialSyncComplete],
  )

  const groupWatchStatus = useGroupWatchVisibility({
    groupWatch,
    groupWatchId,
    hasSignedIn: authStatus.hasSignedIn,
    mayRefetch: refetchCount.current < maxRefetches,
    onGroupIsNotLive,
    onUserSignedOut: () =>
      buttonHandlers.groupWatchGuestScreen({
        groupWatchId,
        replaceScreen: true,
      }),
    refetchGroup,
  })

  const { activeUsers, addActiveUser, isCurrentUser, isNewUserDevicePair, removeActiveUser } =
    useActiveUserSync({
      currentUserId: authStatus.id,
      groupWatch,
      refetchGroup: handleRefetchGroupWatch,
      socketConnected: socket?.connected,
      emitGetActiveUsers: websocketHandlers.emitGetActiveUsers,
      emitJoin: websocketHandlers.emitJoin,
    })

  const handleUserActiveChange = useCallback(
    ({ memberId, active, isTV: receivedIsTV, deviceId }) => {
      if (isNewUserDevicePair({ memberId, deviceId })) {
        addActiveUser({ memberId, deviceId })
      } else if (!active) {
        removeActiveUser({ memberId, deviceId })
      }

      if (receivedIsTV && isCurrentUser(memberId)) {
        const hasStartedTV = !isTV && !userIsWatchingOnTV && active
        const hasQuitTV = !isTV && userIsWatchingOnTV && !active
        setUserIsWatchingOnTV(active)

        if (hasStartedTV) {
          resyncVideoAndPause()
        } else if (hasQuitTV) {
          resyncVideo()
        }
      }
    },
    [
      isNewUserDevicePair,
      isCurrentUser,
      addActiveUser,
      removeActiveUser,
      isTV,
      userIsWatchingOnTV,
      resyncVideoAndPause,
      resyncVideo,
    ],
  )

  // Listeners
  useEffect(() => {
    if (socket) {
      // since other dependencies than the socket can cause this effect to run again
      // we need to remove existing listeners that use stale references
      socket.removeAllListeners()

      socket.on(EVENTS.CHAT, addNewMessage)
      socket.on(EVENTS.PLAYBACK_STATUS, (status) => {
        if (!isTV && userIsWatchingOnTV) return
        onPlaybackStatusChange(status)
      })

      // User active state listeners
      socket.on(EVENTS.GET_ACTIVE_USERS, ({ memberId }) => {
        handleUserActiveChange({ memberId, active: true })
        emitActiveState(true)
      })
      socket.on(EVENTS.ACTIVE_STATE, handleUserActiveChange)
      socket.on(EVENTS.JOIN, handleUserActiveChange)

      socket.on(EVENTS.CLOSE, onGroupIsNotLive)
      socket.on('reconnect_failed', onReconnectFailed)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, handleUserActiveChange, emitActiveState, onPlaybackStatusChange, addNewMessage])

  return {
    ...groupWatchStatus,
    activeUsers,
    chatMessages,
    groupDetailsLoading,
    groupWatch,
    groupWatchError,
    refetchGroup: handleRefetchGroupWatch,
    socketConnected,
    userIsWatchingOnTV,
    videoPlaybackStatus,
    websocketHandlers,
  }
}

const playbackStatusTextMap = {
  [playbackEvents.SKIP_PAUSE]: 'Skipped to',
  [playbackEvents.SKIP_PLAY]: 'Skipped to',
  [playbackEvents.PLAY]: 'Started playing from',
  [playbackEvents.PAUSE]: 'Paused at',
}
